import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, MapPin, Calendar, Building2, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';

export const CaseStudyTemplate = ({ project }) => {
  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light mb-2 text-[#1A1A1A]">Projekt nicht gefunden</h1>
          <Link to="/projekte" className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors">Zurück zu Projekte</Link>
        </div>
      </div>
    );
  }

  const { title, shortDescription, category, client, scope, location, year, challenge, solution, implementation, result, coverImage, galleryImages, tags, ctaLabel, ctaLink } = project;

  return (
    <div data-testid="case-study-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-end pb-10 md:pb-16">
        <div className="absolute inset-0 overflow-hidden">
          {coverImage?.url ? (
            <>
              <img src={coverImage.url} alt={coverImage.alt || title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#F5F2ED] via-black/70 to-black/50" />
            </>
          ) : (
            <>
              <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-black/[0.03] rounded-full blur-[120px]" />
              <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-black/[0.02] rounded-full blur-[100px]" />
            </>
          )}
        </div>

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 w-full">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="mb-6 md:mb-8">
            <Link to="/projekte" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Zurück zu Projekte
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-3 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider bg-white/10 rounded-full mb-4 sm:mb-6">
              {category}
            </span>
            <h1 data-testid="case-study-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] mb-4 sm:mb-6 text-white">
              {title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">{shortDescription}</p>
          </motion.div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-10 md:py-16 border-t border-black/[0.05]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {client && (
              <div className="p-4 sm:p-6 rounded-2xl sm:rounded-[20px] bg-black/[0.02] border border-black/[0.07]">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#9A9A9A] mb-2 sm:mb-3" />
                <p className="text-[10px] sm:text-xs text-[#9A9A9A] uppercase tracking-wider mb-1">Kunde</p>
                <p data-testid="case-study-client" className="text-sm sm:text-base font-medium leading-snug">{client}</p>
              </div>
            )}
            {scope && (
              <div className="p-4 sm:p-6 rounded-2xl sm:rounded-[20px] bg-black/[0.02] border border-black/[0.07]">
                <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-[#9A9A9A] mb-2 sm:mb-3" />
                <p className="text-[10px] sm:text-xs text-[#9A9A9A] uppercase tracking-wider mb-1">Umfang</p>
                <p data-testid="case-study-scope" className="text-sm sm:text-base font-medium leading-snug">{scope}</p>
              </div>
            )}
            {location && (
              <div className="p-4 sm:p-6 rounded-2xl sm:rounded-[20px] bg-black/[0.02] border border-black/[0.07]">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#9A9A9A] mb-2 sm:mb-3" />
                <p className="text-[10px] sm:text-xs text-[#9A9A9A] uppercase tracking-wider mb-1">Standort</p>
                <p className="text-sm sm:text-base font-medium leading-snug">{location}</p>
              </div>
            )}
            {year && (
              <div className="p-4 sm:p-6 rounded-2xl sm:rounded-[20px] bg-black/[0.02] border border-black/[0.07]">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#9A9A9A] mb-2 sm:mb-3" />
                <p className="text-[10px] sm:text-xs text-[#9A9A9A] uppercase tracking-wider mb-1">Jahr</p>
                <p className="text-sm sm:text-base font-medium leading-snug">{year}</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      {coverImage?.url && (
        <section className="py-4 md:py-8">
          <div className="max-w-[1200px] mx-auto px-4 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="aspect-[16/9] md:aspect-[21/9] rounded-2xl md:rounded-[28px] overflow-hidden border border-black/[0.07]">
              <img src={coverImage.url} alt={coverImage.alt || title} className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </section>
      )}

      {/* Challenge & Solution */}
      {(challenge || solution) && (
        <section className="py-16 md:py-24 lg:py-32">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-10 md:gap-16">
              {challenge && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                  <p className="text-xs sm:text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-3 sm:mb-4">Herausforderung</p>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 sm:mb-6">Die Aufgabe</h2>
                  <p data-testid="case-study-challenge" className="text-base sm:text-lg text-[#1A1A1A]/70 leading-[1.75]">{challenge}</p>
                </motion.div>
              )}
              {solution && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                  <p className="text-xs sm:text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-3 sm:mb-4">Lösung</p>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 sm:mb-6">Unser Ansatz</h2>
                  <p data-testid="case-study-solution" className="text-base sm:text-lg text-[#1A1A1A]/70 leading-[1.75]">{solution}</p>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Implementation */}
      {implementation && (
        <section className="py-16 md:py-24 lg:py-32 bg-black/[0.02]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl">
              <p className="text-xs sm:text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-3 sm:mb-4">Umsetzung</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 sm:mb-6">Die Realisierung</h2>
              <p className="text-base sm:text-lg text-[#1A1A1A]/70 leading-[1.75]">{implementation}</p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {galleryImages && galleryImages.length > 0 && (
        <section className="py-24 md:py-32">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
              <p className="text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-4">Impressionen</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Galerie</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6">
              {galleryImages.map((img, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="aspect-[4/3] rounded-[20px] overflow-hidden bg-[#E8E4DD] border border-black/[0.07] group">
                  <img src={img.url} alt={img.alt || ''} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Result */}
      {result && (
        <section className="py-24 md:py-32 bg-black/[0.02]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl">
              <p className="text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-4">Ergebnis</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">Das Resultat</h2>
              <p data-testid="case-study-result" className="text-lg text-[#1A1A1A]/70 leading-relaxed">{result}</p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Tags */}
      {tags && tags.length > 0 && (
        <section className="py-16 border-t border-black/[0.05]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="flex flex-wrap gap-3">
              {tags.map((tag) => (
                <span key={tag} className="px-4 py-2 text-sm text-[#6B6B6B] bg-black/[0.03] border border-black/[0.07] rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-black/[0.07] p-12 md:p-20">
            <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-black/[0.03] rounded-full blur-[100px]" />
            <div className="relative text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">Ähnliches Projekt geplant?</h2>
              <p className="text-lg text-[#6B6B6B] mb-10">Lassen Sie uns über Ihre Anforderungen sprechen – unverbindlich und persönlich.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to={ctaLink || '/kontakt'}>
                  <Button size="lg">{ctaLabel || 'Projekt besprechen'}<ArrowRight className="ml-2 h-5 w-5" /></Button>
                </Link>
                <Link to="/projekte">
                  <Button variant="secondary" size="lg">Weitere Projekte</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyTemplate;
